/*
 * File: ChargeModel.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 09/17/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: The data model representing a charge model inside of the charges array in the post charge model
 */
import { cast, Instance, types } from "mobx-state-tree";
import mstIsNumber, { ValueOrNumeric } from "utils/MSTIsNumber";
import DiagnosisModel from "./DiagnosisModel";

// The base type containing all of the stored properties. This is used as a type in some of the actions to avoid creating a circular type definition.
const ChargeModelProperties = types.model({
  procedureDate: types.maybe(types.string),
  procedure: types.optional(types.string, ""),
  description: types.optional(types.string, ""),
  modifier1: types.optional(types.string, ""),
  modifier2: types.optional(types.string, ""),
  modifier3: types.optional(types.string, ""),
  modifier4: types.optional(types.string, ""),
  tooth: types.maybe(types.string),
  surface: types.optional(types.string, ""),
  units: types.optional(types.number, 0),
  unitFee: types.maybe(types.number),
  diagnosisIds: types.optional(types.array(types.number), []),
  diagnosisModels: types.optional(types.array(DiagnosisModel), []),
  calculationMethod: types.optional(types.number, 0)
});

export type ChargeModelUpdate = {
  procedure: string;
  description: string;
  unitFee: number;
  units: number;
  calculationMethod: number;
};

// The following actions calls are split because a single invocation can't reference methods defined within itself. So any methods which need access to other actions need to be split up to satisfy tsc
const ChargeModel = ChargeModelProperties.actions(self => ({
  update: <K extends keyof Instance<typeof ChargeModelProperties>>(
    key: K,
    value: ValueOrNumeric<Instance<typeof ChargeModelProperties>[K]>
  ) => {
    if (!(key in self)) {
      throw new Error(`Invalid key "${key}" used for ChargeModel update`);
    }
    self[key] = mstIsNumber(self, key, value);
  },
  getDiagnosisWithId: (umlsId: number | undefined) => {
    const diagnosis = self.diagnosisModels.find(
      diagnosis => diagnosis.umlsId === umlsId
    );
    return diagnosis;
  }
}))
  .actions(self => ({
    diagnosisIsToggledOn(diagnosisId: number) {
      const diagnosis = self.getDiagnosisWithId(diagnosisId);
      return diagnosis?.toggledOn;
    },
    toggleDiagnosis(shouldTurnDiagnosisOn: boolean, diagnosisId: number) {
      const diagnosis = self.getDiagnosisWithId(diagnosisId);
      if (diagnosis !== undefined) {
        diagnosis.toggledOn = shouldTurnDiagnosisOn;
      }
    }
  }))
  .actions(self => ({
    setSortedDiagnoses(diagnoses: Instance<typeof DiagnosisModel>[]) {
      diagnoses.forEach((diagnosis, index) => {
        const chargeDiagnosis = self.getDiagnosisWithId(diagnosis.umlsId);
        chargeDiagnosis?.setOrder(index);
      });
      self.diagnosisModels = cast(diagnoses);
    },

    handleSwitchToggle(diagnosisId: number, checked: boolean) {
      if (checked && !self.diagnosisIsToggledOn(diagnosisId)) {
        self.toggleDiagnosis(true, diagnosisId);
      } else if (!checked) {
        self.toggleDiagnosis(false, diagnosisId);
      }
    },

    addDiagnosis(
      diagnosisId: number,
      index: number | undefined,
      diagnosis: string | undefined,
      pointer: string | undefined,
      code: string | undefined
    ) {
      const diagnosisModel = DiagnosisModel.create({
        umlsId: diagnosisId,
        order: index,
        diagnosis: diagnosis,
        pointer: pointer,
        code: code
      });
      self.diagnosisModels.push(diagnosisModel);
    }
  }))
  .views(self => ({
    get sortedDiagnoses() {
      const sortedArray = self.diagnosisModels.slice().sort((a, b) => {
        return (a.order ?? 0) - (b.order ?? 0);
      });
      return sortedArray;
    },
    getDiagnosisIds() {
      const selectedDiagnoses = self.diagnosisModels.filter(dx => dx.toggledOn);
      const ids = [];
      for (const dxModel of selectedDiagnoses) {
        if (dxModel.umlsId != undefined) {
          ids.push(dxModel.umlsId);
        }
      }
      return ids;
    }
  }));

export default ChargeModel;
